import { defineStore } from 'pinia';
import { ref, computed, shallowRef } from 'vue';
import { getSocketServiceInstance } from '@/shared/services/socket';
import {
  getCallToken,
  clearActiveCalls,
  refreshCallToken,
  endCallRest,
  cancelCallRest,
  checkActiveCall,
  rejectCallFromNotificationRest,
} from '@/domains/chat/services/calls.api';
import { registerCallInviteWorker } from '@/domains/chat/utils/registerCallInviteWorker';
import { userStore } from '@/shared/store/userStore';
import { useAuthStore } from '@/domains/auth/store/authStore';
import { useLiveStore } from '@/domains/live/store/liveStore';
import { Room } from 'livekit-client';

const CALL_STATES = {
  IDLE: 'idle',
  DIALING: 'dialing',
  RINGING: 'ringing',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ENDED: 'ended',
  ONGOING_ELSEWHERE: 'ongoing_elsewhere',
  /** Incoming accept failed / timed out — show Rejoin or Close (missed or stale call). */
  INCOMING_UNRESOLVED: 'incoming_unresolved',
};

/** If the server never acks call:accept, stop leaving the user stuck on “ringing”. */
const ACCEPT_ACK_TIMEOUT_MS = 12000;

function isRealtimeSessionActive() {
  try {
    const authStore = useAuthStore();
    return !authStore.isLoggedOut && !authStore.shouldPreventApiCall;
  } catch {
    return true;
  }
}

function normalizeAck(data) {
  if (Array.isArray(data) && data.length > 0 && data[0] && typeof data[0] === 'object') {
    return data[0];
  }
  return data && typeof data === 'object' ? data : {};
}

// NOTE: These module-level variables are per-tab. Each browser tab has its own
// copy of this module, so calls are effectively handled per-tab (no
// cross-tab coordination). This is an intentional decision: only the active
// tab is expected to handle calls on web.
// Track IDs to make signaling more robust on web within a single tab:
// - activeIncomingCallId: the call currently ringing in this tab
// - blockedCallIds: calls that have already reached a terminal event (ended/cancelled/rejected)
//   so late call:incoming for the same id can be ignored.
let activeIncomingCallId = null;
const blockedCallIds = new Set();
let busyClearAttempted = false;
let tokenRefreshTimer = null;
let callerConnectingTimeout = null;
let tokenRefreshInProgress = false;
let isConnectingLiveKit = false; // Guard against race condition on accept

/** Debounced active-call restore (items 5 & 8): focus, reconnect, optional initial tick */
let callLifecycleInstalled = false;
let callLifecycleDebounceTimer = null;
let callLifecycleConnSuccess = null;
let callLifecycleVisibility = null;
let callLifecycleInitialTimer = null;
/** Polls GET /calls/active/check while the caller is dialing/ringing so accept/reject still updates UI if socket events are missed. */
let outgoingReconcileTimer = null;

const STALE_BUSY_CLEAR_REASONS = new Set([
  'CALLER_BUSY',
  'CALLEE_BUSY',
  'USER_BUSY',
  'ACTIVE_RINGING_CALL',
]);

let callSwMessageHandler = null;

function parseNotificationData(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') return raw;
  return {};
}

export const useCallStore = defineStore('callStore', () => {
  const session = ref(null);
  const callState = ref(CALL_STATES.IDLE);
  const errorMessage = ref(null);
  const room = shallowRef(null);
  const localVideoTrack = shallowRef(null);
  const remoteVideoTrack = shallowRef(null);
  const remoteAudioTrack = shallowRef(null);
  const isMuted = ref(false);
  const isVideoEnabled = ref(true);
  const isAccepted = ref(false);

  const hasActiveCall = computed(() => {
    const s = callState.value;
    return s === CALL_STATES.DIALING || s === CALL_STATES.RINGING || s === CALL_STATES.CONNECTING || s === CALL_STATES.CONNECTED || s === CALL_STATES.RECONNECTING;
  });
  const hasIncomingCall = computed(() => callState.value === CALL_STATES.RINGING && session.value?.isIncoming);
  const isVideoCall = computed(() => session.value?.type === 'VIDEO');
  const showCallScreen = computed(() => {
    const s = callState.value;
    const incoming = session.value?.isIncoming;
    return s === CALL_STATES.DIALING || s === CALL_STATES.CONNECTING || s === CALL_STATES.CONNECTED || s === CALL_STATES.RECONNECTING ||
      (s === CALL_STATES.RINGING && !incoming);
  });
  const remoteDisplayName = computed(() => {
    const s = session.value;
    if (!s) return '';
    // For outgoing calls, the remote party is the callee (receiver)
    // For incoming calls, the remote party is the caller
    if (s.isIncoming) {
      return s.callerName || s.receiverName || 'Unknown';
    }
    return s.calleeName || s.receiverName || s.callerName || 'Unknown';
  });
  const remoteAvatarUrl = computed(() => {
    const s = session.value;
    if (!s) return null;
    // For outgoing calls, the remote party is the callee (receiver)
    // For incoming calls, the remote party is the caller
    if (s.isIncoming) {
      return s.callerAvatar || s.receiverPhotoUrl || null;
    }
    return s.calleeAvatarUrl || s.receiverPhotoUrl || s.callerAvatar || null;
  });

  let socketOffFns = [];
  let roomCleanup = null;

  function clearOutgoingReconcileTimer() {
    if (outgoingReconcileTimer) {
      clearInterval(outgoingReconcileTimer);
      outgoingReconcileTimer = null;
    }
  }

  function startOutgoingReconcilePolling() {
    clearOutgoingReconcileTimer();
    outgoingReconcileTimer = setInterval(() => {
      void reconcileOutgoingCallWithServer();
    }, 2800);
  }

  /**
   * Caller-only: server may move to ANSWERED or drop the call (reject/miss) while `call:accepted` / `call:rejected` never arrive (multi-pod, reconnect, etc.).
   */
  async function reconcileOutgoingCallWithServer() {
    const sess = session.value;
    if (!sess?.callId || sess.isIncoming) return;
    const st = callState.value;
    if (st !== CALL_STATES.RINGING && st !== CALL_STATES.DIALING) return;

    const myCallId = String(sess.callId);
    let res;
    try {
      res = await checkActiveCall();
    } catch (e) {
      console.warn('[callStore] reconcileOutgoingCallWithServer checkActiveCall failed', e);
      return;
    }

    if (callState.value !== st || String(session.value?.callId || '') !== myCallId) return;

    if (!res?.hasActiveCall || !res.call?.id) {
      blockedCallIds.add(myCallId);
      setState(CALL_STATES.ENDED);
      cleanupRoom();
      return;
    }

    if (String(res.call.id) !== myCallId) return;

    const status = String(res.call.status || '').toUpperCase();
    if (status !== 'ANSWERED') return;

    if (
      callState.value === CALL_STATES.CONNECTING ||
      callState.value === CALL_STATES.CONNECTED ||
      room.value
    ) {
      return;
    }

    const c = res.call;
    try {
      const tok = await getCallToken(myCallId);
      if (!tok.canJoin || !tok.token || !tok.liveKitWsUrl) return;
      if (String(session.value?.callId || '') !== myCallId) return;

      const answeredType = String(c.type || 'VOICE').toUpperCase();
      isVideoEnabled.value = answeredType === 'VIDEO';
      session.value = {
        ...session.value,
        callId: myCallId,
        type: answeredType,
        isIncoming: false,
        conversationId: c.conversationId,
        receiverId: c.calleeId,
        calleeId: c.calleeId,
        calleeName: c.callee?.username || session.value.calleeName,
        calleeAvatarUrl: c.callee?.profilePictureUrl ?? session.value.calleeAvatarUrl,
        callerId: c.callerId,
        token: tok.token,
        liveKitWsUrl: tok.liveKitWsUrl,
        liveKitRoomId: tok.liveKitRoomId || c.liveKitRoomId,
        roomName: tok.liveKitRoomId || c.liveKitRoomId,
      };
      isAccepted.value = true;
      errorMessage.value = null;
      callState.value = CALL_STATES.CONNECTING;
      clearOutgoingReconcileTimer();
      await connectToLiveKit(session.value);
    } catch (e) {
      console.warn('[callStore] reconcileOutgoingCallWithServer connect failed', e);
    }
  }

  function setState(newState) {
    callState.value = newState;
    if (newState === CALL_STATES.ENDED || newState === CALL_STATES.IDLE) {
      clearOutgoingReconcileTimer();
      session.value = null;
      // Keep errorMessage so ChatIndex watcher can show toast; cleared after toast or on next initiate
      activeIncomingCallId = null;
      busyClearAttempted = false;
      isAccepted.value = false;
      isMuted.value = false;
      isVideoEnabled.value = true;
      if (tokenRefreshTimer) {
        clearInterval(tokenRefreshTimer);
        tokenRefreshTimer = null;
      }
      if (callerConnectingTimeout) {
        clearTimeout(callerConnectingTimeout);
        callerConnectingTimeout = null;
      }
    }

    if (newState === CALL_STATES.CONNECTED) {
      clearOutgoingReconcileTimer();
    }

    // When a call transitions into CONNECTED, start periodic token refresh for long calls.
    if (newState === CALL_STATES.CONNECTED && session.value?.callId) {
      if (tokenRefreshTimer) {
        clearInterval(tokenRefreshTimer);
        tokenRefreshTimer = null;
      }
      const callIdForRefresh = session.value.callId;
      // Refresh slightly before the typical 5-minute TTL (e.g. every 4 minutes).
      const REFRESH_INTERVAL_MS = 4 * 60 * 1000;
      tokenRefreshTimer = setInterval(() => {
        void (async () => {
          if (tokenRefreshInProgress) return;
          if (callState.value !== CALL_STATES.CONNECTED) return;
          if (!session.value || session.value.callId !== callIdForRefresh) return;
          tokenRefreshInProgress = true;
          try {
            const res = await refreshCallToken(callIdForRefresh);
            if (!res?.token) return;
            if (!session.value || session.value.callId !== callIdForRefresh) return;
            const next = {
              ...session.value,
              token: res.token,
              liveKitWsUrl: res.liveKitWsUrl || session.value.liveKitWsUrl,
              liveKitRoomId: res.liveKitRoomId || session.value.liveKitRoomId,
              roomName: res.liveKitRoomId || session.value.roomName,
            };
            session.value = next;
            await reconnectLiveKitWithNewToken(next);
          } catch (e) {
            console.warn('[callStore] refreshCallToken failed', e);
          } finally {
            tokenRefreshInProgress = false;
          }
        })();
      }, REFRESH_INTERVAL_MS);
    }

    // Caller-side safety: if we are CONNECTED but the callee has not yet accepted
    // (isAccepted === false) for too long, treat as a failed connection.
    if (
      newState === CALL_STATES.CONNECTED &&
      session.value &&
      session.value.isIncoming === false &&
      !isAccepted.value
    ) {
      if (callerConnectingTimeout) {
        clearTimeout(callerConnectingTimeout);
      }
      const MAX_CONNECTING_MS = 25000;
      const callIdForTimeout = session.value.callId;
      callerConnectingTimeout = setTimeout(() => {
        if (
          callState.value === CALL_STATES.CONNECTED &&
          !isAccepted.value &&
          session.value &&
          session.value.callId === callIdForTimeout
        ) {
          errorMessage.value = 'Call failed to connect';
          setState(CALL_STATES.ENDED);
          cleanupRoom();
        }
      }, MAX_CONNECTING_MS);
    }

    // If we move away from CONNECTED or the call gets accepted, clear the timeout.
    if (
      callerConnectingTimeout &&
      (newState !== CALL_STATES.CONNECTED || isAccepted.value)
    ) {
      clearTimeout(callerConnectingTimeout);
      callerConnectingTimeout = null;
    }
  }

  function cleanupRoom() {
    if (roomCleanup) {
      roomCleanup();
      roomCleanup = null;
    }
    if (room.value) {
      try {
        room.value.disconnect(true);
      } catch (_) {
        /* ignore */
      }
      room.value = null;
    }
    localVideoTrack.value = null;
    remoteVideoTrack.value = null;
    remoteAudioTrack.value = null;
    room.value = null;
  }

  /** Shared path for Socket `notification_created`, SW postMessage, etc. */
  function applyPushStyleIncomingHint(data, nType) {
    const callId = data?.callId ? String(data.callId) : '';
    const screen = data?.screen;
    const typeStr = String(nType || data?.type || '').toUpperCase();
    const typeLower = String(nType || data?.type || '').toLowerCase();
    const isCallHint =
      typeStr === 'VOICE_CALL' ||
      typeStr === 'VIDEO_CALL' ||
      screen === 'call' ||
      typeLower.includes('call') ||
      (callId && (data?.liveKitRoomId || data?.callerName !== undefined));
    if (!callId || !isCallHint) return;
    if (hasActiveCall.value) return;
    if (blockedCallIds.has(callId)) return;
    if (activeIncomingCallId === callId) return;
    const me = getCurrentUserId();
    if (me && data.callerId && String(data.callerId) === String(me)) return;
    const typeUpper = String(
      data.callType || (typeStr === 'VIDEO_CALL' ? 'VIDEO' : 'VOICE'),
    ).toUpperCase();
    activeIncomingCallId = callId;
    isAccepted.value = false;
    session.value = {
      callId,
      type: typeUpper === 'VIDEO' ? 'VIDEO' : 'VOICE',
      callerId: data.callerId ? String(data.callerId) : '',
      callerName: data.callerName || '',
      callerAvatar: data.callerAvatar || null,
      liveKitRoomId: data.liveKitRoomId || null,
      liveKitWsUrl: data.liveKitWsUrl || null,
      conversationId: data.conversationId,
      isIncoming: true,
      roomToken:
        data.roomToken != null && String(data.roomToken).trim() !== ''
          ? String(data.roomToken)
          : null,
    };
    callState.value = CALL_STATES.RINGING;
    errorMessage.value = null;
    isVideoEnabled.value = typeUpper === 'VIDEO';
  }

  function installCallSwMessageBridge() {
    if (callSwMessageHandler || typeof navigator === 'undefined' || !navigator.serviceWorker?.addEventListener) {
      return;
    }
    callSwMessageHandler = (event) => {
      const msg = event.data;
      if (!msg || msg.type !== 'CALL_INVITE') return;
      const raw = msg.payload;
      const data =
        raw && typeof raw === 'object' && !Array.isArray(raw)
          ? raw
          : parseNotificationData(raw);
      applyPushStyleIncomingHint(data, msg.notificationType ?? data?.type);
    };
    navigator.serviceWorker.addEventListener('message', callSwMessageHandler);
  }

  function removeCallSwMessageBridge() {
    if (!callSwMessageHandler || typeof navigator === 'undefined') return;
    navigator.serviceWorker.removeEventListener('message', callSwMessageHandler);
    callSwMessageHandler = null;
  }

  function attachSocketListeners() {
    if (typeof socketOffFns === 'function') {
      socketOffFns();
    }
    const socket = getSocketServiceInstance();

    const onIncoming = (data) => {
      if (!isRealtimeSessionActive()) return;
      const raw = Array.isArray(data) && data[0] ? data[0] : data;
      const payload = raw?.call && typeof raw.call === 'object' ? raw.call : raw;
      const callId = payload?.callId ?? payload?.id ?? '';
      const callerId = payload?.callerId ?? '';
      const callerName = payload?.callerName ?? '';
      const type = (payload?.type ?? 'VOICE').toUpperCase();
      const liveKitRoomId = payload?.liveKitRoomId ?? payload?.liveKitRoomId;
      const liveKitWsUrl = payload?.liveKitWsUrl ?? payload?.liveKitWsUrl;
      const callerAvatar = payload?.callerAvatar ?? payload?.profilePictureUrl ?? payload?.callerPhotoUrl;
      const roomToken =
        payload?.roomToken != null && String(payload.roomToken).trim() !== ''
          ? String(payload.roomToken)
          : null;
      if (!callId) return;

      // If this callId has already reached a terminal state, ignore late incoming.
      if (blockedCallIds.has(callId)) {
        return;
      }

      // Deduplicate same incoming callId in this tab.
      if (activeIncomingCallId && activeIncomingCallId === callId) {
        return;
      }

      // If user is already in an active call, ignore new incoming
      // so we don't show a popup on top of an existing call.
      if (hasActiveCall.value) {
        // Optionally, we could auto-reject here by emitting call:reject,
        // but for now we just ignore to keep UX simple on web.
        return;
      }

      activeIncomingCallId = callId;
      isAccepted.value = false;
      session.value = {
        callId,
        type,
        callerId,
        callerName,
        callerAvatar,
        liveKitRoomId,
        liveKitWsUrl,
        conversationId: payload?.conversationId,
        isIncoming: true,
        roomToken,
      };
      callState.value = CALL_STATES.RINGING;
      errorMessage.value = null;
      isVideoEnabled.value = type === 'VIDEO';
    };

    const onNotificationCreated = (notification) => {
      if (!isRealtimeSessionActive()) return;
      const data = parseNotificationData(notification?.data);
      const nType = notification?.type || data?.type;
      applyPushStyleIncomingHint(data, nType);
    };

    // Backend does not emit call:transferred yet; listener kept for forward-compatible transfer API.
    const onTransferred = (data) => {
      const ack = normalizeAck(data);
      const id =
        ack.callId ??
        ack.call?.id ??
        (typeof ack.call === 'string' ? ack.call : null) ??
        session.value?.callId;
      if (!id || session.value?.callId !== id) return;
      if (callState.value !== CALL_STATES.CONNECTED) return;
      const token = ack.token ?? ack.call?.token;
      const liveKitWsUrl = ack.liveKitWsUrl ?? ack.call?.liveKitWsUrl;
      const liveKitRoomId = ack.liveKitRoomId ?? ack.call?.liveKitRoomId;
      if (!token || !liveKitWsUrl) return;
      const sess = session.value;
      const roomName = liveKitRoomId || sess.liveKitRoomId || sess.roomName || id;
      session.value = {
        ...sess,
        token: String(token),
        liveKitWsUrl: String(liveKitWsUrl),
        liveKitRoomId: roomName,
        roomName,
      };
      void reconnectLiveKitWithNewToken(session.value);
    };

    const onInitiated = (data) => {
      if (session.value?.isIncoming || callState.value !== CALL_STATES.DIALING) return;
      const ack = normalizeAck(data);
      if (!ack.success) {
        errorMessage.value = ack.error || ack.reason || 'Call failed';
        setState(CALL_STATES.ENDED);
        return;
      }
      const token = ack.token;
      const liveKitRoomId = ack.liveKitRoomId ?? ack.call?.liveKitRoomId;
      const liveKitWsUrl = ack.liveKitWsUrl ?? ack.call?.liveKitWsUrl;
      const callId = ack.callId ?? ack.call?.id ?? liveKitRoomId;
      if (!token || !liveKitRoomId || !liveKitWsUrl) {
        errorMessage.value = 'No token or room info';
        setState(CALL_STATES.ENDED);
        return;
      }
      if (session.value) {
        session.value = { ...session.value, callId, token, liveKitRoomId, liveKitWsUrl, roomName: liveKitRoomId };
      }
      // Stay on ringing until the callee answers — do not join LiveKit yet (avoids "Connecting" while they ring).
      callState.value = CALL_STATES.RINGING;
      startOutgoingReconcilePolling();
    };

    const onAccepted = (data) => {
      const ack = normalizeAck(data);
      if (!session.value) return;
      // Backend emits flat { callId, liveKitWsUrl, ... }; Prisma ack may use call.id — support both.
      const ackCallId =
        ack.callId ??
        ack.call?.id ??
        extractCallId(data);
      if (!ackCallId) return;
      let sess = session.value;
      if (!sess.callId) {
        session.value = { ...sess, callId: String(ackCallId) };
        sess = session.value;
      } else if (String(sess.callId) !== String(ackCallId)) {
        return;
      }
      if (
        callState.value === CALL_STATES.CONNECTING ||
        callState.value === CALL_STATES.CONNECTED ||
        room.value
      ) {
        return;
      }

      const ackToken = ack.token ?? ack.call?.token;
      const liveKitWsUrl = ack.liveKitWsUrl ?? ack.call?.liveKitWsUrl;
      const liveKitRoomId = ack.liveKitRoomId ?? ack.call?.liveKitRoomId;

      if (sess.isIncoming) {
        const roomTok =
          sess.roomToken && String(sess.roomToken).trim() !== '' ? sess.roomToken : null;
        let effectiveToken =
          ackToken != null && String(ackToken).trim() !== '' ? String(ackToken) : null;
        if (!effectiveToken && sess.token && String(sess.token).trim() !== '') {
          effectiveToken = String(sess.token);
        }
        if (!effectiveToken && roomTok) effectiveToken = roomTok;
        if (!effectiveToken) return;
        const url = liveKitWsUrl || sess.liveKitWsUrl;
        const roomName =
          liveKitRoomId || sess.liveKitRoomId || sess.roomName || sess.callId;
        session.value = { ...sess, token: effectiveToken };
        if (url && roomName) {
          isAccepted.value = true;
          clearOutgoingReconcileTimer();
          connectToLiveKit({
            ...session.value,
            liveKitWsUrl: url,
            liveKitRoomId: roomName,
            roomName,
          });
        }
        return;
      }

      // Outgoing: peer answered — join LiveKit now (until then UI stays "ringing", not "connecting").
      // We MUST prioritize our own token granted during initiate. The ackToken in 
      // call:accepted belongs to the callee, not the caller!
      let effectiveToken = sess.token && String(sess.token).trim() !== '' ? String(sess.token) : null;
      if (!effectiveToken && ackToken != null && String(ackToken).trim() !== '') {
        effectiveToken = String(ackToken);
      }
      let url = liveKitWsUrl || sess.liveKitWsUrl;
      let roomName = liveKitRoomId || sess.liveKitRoomId || sess.roomName || sess.callId;
      if (effectiveToken && url && roomName) {
        session.value = {
          ...sess,
          token: effectiveToken,
          liveKitWsUrl: url,
          liveKitRoomId: roomName,
          roomName,
        };
        isAccepted.value = true;
        clearOutgoingReconcileTimer();
        connectToLiveKit(session.value);
        return;
      }
      getCallToken(sess.callId)
        .then((res) => {
          if (!res?.canJoin || !res.token || !res.liveKitWsUrl) return;
          if (callState.value === CALL_STATES.ENDED || !session.value || String(session.value.callId) !== String(sess.callId)) return;
          const next = {
            ...session.value,
            token: res.token,
            liveKitWsUrl: res.liveKitWsUrl,
            liveKitRoomId: res.liveKitRoomId || session.value.liveKitRoomId || sess.callId,
            roomName: res.liveKitRoomId || session.value.roomName || sess.callId,
          };
          session.value = next;
          isAccepted.value = true;
          clearOutgoingReconcileTimer();
          connectToLiveKit(next);
        })
        .catch((e) => {
          console.warn('[callStore] call:accepted caller getCallToken failed', e);
        });
    };

    const extractCallId = (data) => {
      try {
        const raw = Array.isArray(data) && data[0] ? data[0] : data;
        if (raw && typeof raw === 'object') {
          return raw.callId || raw.id || (raw.call && (raw.call.id || raw.call.callId)) || null;
        }
        return null;
      } catch {
        return null;
      }
    };

    const onRejected = (data) => {
      const id = extractCallId(data);
      if (id) {
        blockedCallIds.add(id);
        if (String(session.value?.callId || '') === String(id)) {
          activeIncomingCallId = null;
        }
      }
      if (id && session.value?.callId && String(session.value.callId) !== String(id)) {
        return;
      }
      errorMessage.value = 'Call rejected';
      clearOutgoingReconcileTimer();
      setState(CALL_STATES.ENDED);
    };

    const onCancelled = (data) => {
      const id = extractCallId(data);
      if (id) {
        blockedCallIds.add(id);
        if (session.value?.callId === id) {
          activeIncomingCallId = null;
        }
      }
      // Only end the call if this cancellation is for the current session
      if (id && session.value?.callId && String(session.value.callId) !== String(id)) {
        return;
      }
      errorMessage.value = 'Call cancelled';
      setState(CALL_STATES.ENDED);
      cleanupRoom();
    };

    const onEnded = (data) => {
      const id = extractCallId(data);
      if (id) {
        blockedCallIds.add(id);
        if (session.value?.callId === id) {
          activeIncomingCallId = null;
        }
      }

      // Only end the call if this event is for the current session
      if (id && session.value?.callId && String(session.value.callId) !== String(id)) {
        return;
      }

      // Try to show a more specific end message when reason is provided.
      const reason = data && typeof data === 'object'
        ? (data.reason || (data.call && data.call.endReason))
        : null;
      if (reason && typeof reason === 'string') {
        const r = reason.toUpperCase();
        if (r === 'CANCELLED') {
          errorMessage.value = 'Call cancelled';
        } else if (r === 'REJECTED') {
          errorMessage.value = 'Call rejected';
        } else if (r === 'NO_ANSWER' || r === 'TIMEOUT') {
          errorMessage.value = 'Missed call';
        } else if (r === 'NETWORK_ERROR' || r === 'TECHNICAL_ERROR') {
          errorMessage.value = 'Connection failed';
        } else if (r === 'COMPLETED') {
          // Completed successfully – no error toast needed.
        }
      }

      setState(CALL_STATES.ENDED);
      cleanupRoom();
    };

    const onBusy = (data) => {
      const reason = data?.reason || data;
      const reasonStr = typeof reason === 'string' ? reason : '';

      void recoverActiveCallAfterServerBusy().then((recovered) => {
        if (recovered) {
          errorMessage.value = null;
          return;
        }
        if (!busyClearAttempted && STALE_BUSY_CLEAR_REASONS.has(reasonStr)) {
          busyClearAttempted = true;
          clearActiveCalls(getCurrentUserId()).catch(() => {});
        }
        errorMessage.value = reasonStr || 'USER_BUSY';
        setState(CALL_STATES.ENDED);
      });
    };

    const onError = (data) => {
      const raw = data && typeof data === 'object' ? data : {};
      const code = raw.reason || raw.code;
      const msg = raw.error ?? raw.message ?? data;
      const asString =
        typeof code === 'string' && code.trim()
          ? code.trim()
          : typeof msg === 'string'
            ? msg
            : 'Call error';
      errorMessage.value = asString;
      setState(CALL_STATES.ENDED);
    };

    socket.on('call:incoming', onIncoming);
    socket.on('notification_created', onNotificationCreated);
    socket.on('call:initiated', onInitiated);
    socket.on('call:accepted', onAccepted);
    socket.on('call:rejected', onRejected);
    socket.on('call:cancelled', onCancelled);
    socket.on('call:ended', onEnded);
    socket.on('call:busy', onBusy);
    socket.on('call:error', onError);
    socket.on('call:transferred', onTransferred);

    // Multi-device events: call was answered or rejected on another device
    const onAnsweredElsewhere = (data) => {
      const raw = normalizeAck(data);
      const id = raw?.callId || raw?.id || extractCallId(data);
      if (!id) return;
      // If the user who answered is the current user, ignore (this device accepted)
      const me = getCurrentUserId();
      const answeredBy = raw?.answeredBy;
      if (me && answeredBy && String(answeredBy) === String(me)) return;
      // Only end if it matches the current session
      if (session.value?.callId && String(session.value.callId) !== String(id)) return;
      blockedCallIds.add(id);
      activeIncomingCallId = null;
      errorMessage.value = 'Call was answered on another device';
      setState(CALL_STATES.ENDED);
      cleanupRoom();
    };

    const onRejectedElsewhere = (data) => {
      const raw = normalizeAck(data);
      const id = raw?.callId || raw?.id || extractCallId(data);
      if (!id) return;
      // If the user who rejected is the current user, ignore
      const me = getCurrentUserId();
      const rejectedBy = raw?.rejectedBy;
      if (me && rejectedBy && String(rejectedBy) === String(me)) return;
      // Only end if it matches the current session
      if (session.value?.callId && String(session.value.callId) !== String(id)) return;
      blockedCallIds.add(id);
      activeIncomingCallId = null;
      errorMessage.value = 'Call was rejected on another device';
      setState(CALL_STATES.ENDED);
      cleanupRoom();
    };

    socket.on('call:answered_elsewhere', onAnsweredElsewhere);
    socket.on('call:rejected_elsewhere', onRejectedElsewhere);

    const off = () => {
      socket.off('call:incoming', onIncoming);
      socket.off('notification_created', onNotificationCreated);
      socket.off('call:initiated', onInitiated);
      socket.off('call:accepted', onAccepted);
      socket.off('call:rejected', onRejected);
      socket.off('call:cancelled', onCancelled);
      socket.off('call:ended', onEnded);
      socket.off('call:busy', onBusy);
      socket.off('call:error', onError);
      socket.off('call:transferred', onTransferred);
      socket.off('call:answered_elsewhere', onAnsweredElsewhere);
      socket.off('call:rejected_elsewhere', onRejectedElsewhere);
    };
    socketOffFns = off;
  }

  /**
   * Disconnect LiveKit and join again with a fresh JWT (required when access token TTL expires).
   */
  async function reconnectLiveKitWithNewToken(sess) {
    if (callState.value !== CALL_STATES.CONNECTED) return;
    if (!sess?.token || !sess?.liveKitWsUrl) return;
    cleanupRoom();
    await connectToLiveKit(sess);
  }

  function syncLocalVideoFromParticipant(lp) {
    if (!lp) return;
    const videoPubs = lp.videoTrackPublications;
    if (videoPubs && typeof videoPubs.forEach === 'function') {
      videoPubs.forEach((pub) => {
        if (pub.track) localVideoTrack.value = pub.track;
      });
    } else if (videoPubs && typeof videoPubs.values === 'function') {
      for (const pub of videoPubs.values()) {
        if (pub.track) {
          localVideoTrack.value = pub.track;
          break;
        }
      }
    }
  }

  async function connectToLiveKit(sess) {
    if (!sess?.token || !sess?.liveKitWsUrl) return;
    if (room.value || isConnectingLiveKit || callState.value === CALL_STATES.CONNECTED) {
      console.warn('[callStore] connectToLiveKit duplicate connection attempt blocked');
      return;
    }
    isConnectingLiveKit = true;
    const url = sess.liveKitWsUrl.startsWith('ws') ? sess.liveKitWsUrl : sess.liveKitWsUrl.replace(/^http/, 'ws');
    callState.value = CALL_STATES.CONNECTING;
    try {
      const r = new Room();
      room.value = r;
      roomCleanup = () => {
        r.removeAllListeners();
        try { r.disconnect(true); } catch (_) {}
      };
      r.on('trackSubscribed', (track) => {
        if (track.kind === 'video') remoteVideoTrack.value = track;
        if (track.kind === 'audio') remoteAudioTrack.value = track;
      });
      r.on('trackUnsubscribed', (track) => {
        if (track.kind === 'video') remoteVideoTrack.value = null;
        if (track.kind === 'audio') remoteAudioTrack.value = null;
      });
      r.on('localTrackPublished', (pub, participant) => {
        if (pub.track?.kind === 'video') localVideoTrack.value = pub.track;
      });
      r.on('localTrackUnpublished', (pub, participant) => {
        if (pub.track?.kind === 'video') localVideoTrack.value = null;
      });
      // Handle LiveKit native reconnection
      r.on('reconnecting', () => {
        if (callState.value !== CALL_STATES.ENDED) {
          setState(CALL_STATES.RECONNECTING);
        }
      });
      r.on('reconnected', () => {
        if (callState.value !== CALL_STATES.ENDED) {
          setState(CALL_STATES.CONNECTED);
        }
      });
      r.on('disconnected', () => {
        // Only end immediately if we aren't already ending
        if (callState.value !== CALL_STATES.ENDED) {
           console.warn('[callStore] LiveKit disconnected unexpectedly');
           setState(CALL_STATES.ENDED);
           errorMessage.value = 'Connection lost';
        }
        cleanupRoom();
      });
      await r.connect(url, sess.token, { autoSubscribe: true });

      // Guard: If the call was cancelled/ended via socket while we were awaiting connection,
      // bail out immediately from the setup process. Avoid resurrecting a dead call.
      if (callState.value === CALL_STATES.ENDED) {
        console.warn('[callStore] Call ended during LiveKit connect, discarding room');
        try { r.disconnect(true); } catch (_) {}
        return;
      }

      const lp = r.localParticipant;
      const isVideoSession = sess.type === 'VIDEO';
      if (isVideoSession) {
        await lp.enableCameraAndMicrophone();
        syncLocalVideoFromParticipant(lp);
        if (!isVideoEnabled.value) {
          await lp.setCameraEnabled(false);
          localVideoTrack.value = null;
        }
      } else {
        await lp.setMicrophoneEnabled(true);
        await lp.setCameraEnabled(false);
        localVideoTrack.value = null;
        if (isVideoEnabled.value) {
          await lp.setCameraEnabled(true);
          syncLocalVideoFromParticipant(lp);
        }
      }
      // Use setState so token-refresh interval and caller connecting watchdog run (not only direct assignment).
      setState(CALL_STATES.CONNECTED);
    } catch (err) {
      console.error('[callStore] LiveKit connect failed', err);
      const msg = (err && err.message) ? String(err.message) : '';
      let friendly = 'Connection failed';

      // Special-case: if the failure is specifically about getUserMedia/mediaDevices,
      // keep the call open and do not surface a toast. Some embedded browsers do not
      // support these APIs, but we don't want to auto-end the session in that case.
      if (msg && (/getUserMedia/i.test(msg) || /mediaDevices/i.test(msg))) {
        console.warn('[callStore] getUserMedia/mediaDevices not available; ignoring to avoid ending call');
        isConnectingLiveKit = false;
        return;
      }

      if (msg.includes('microphone') || msg.includes('Microphone') || /enableMicrophone|is not a function/i.test(msg)) {
        friendly = 'Microphone access failed';
      } else if (msg.includes('camera') || msg.includes('Camera') || /enableCamera|is not a function/i.test(msg)) {
        friendly = 'Camera access failed';
      } else if (msg) {
        friendly = msg;
      }
      errorMessage.value = friendly;
      setState(CALL_STATES.ENDED);
      cleanupRoom();
    } finally {
      isConnectingLiveKit = false;
    }
  }

  function getCurrentUserId() {
    try {
      const u = userStore().getUser();
      return u?.id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Apply GET /calls/active/check result to local session (incoming ring, outgoing ring, or answered join prompt).
   * @returns {Promise<boolean>} true if state was updated
   */
  async function hydrateSessionFromActiveCheckResponse(res) {
    if (!res?.hasActiveCall || !res.call?.id) return false;
    const me = getCurrentUserId();
    if (!me) return false;
    const c = res.call;
    const callId = c.id;
    if (blockedCallIds.has(callId)) return false;
    const status = String(c.status || '').toUpperCase();
    if (status !== 'RINGING' && status !== 'ANSWERED') return false;
    const imCallee = String(c.calleeId) === String(me);
    const imCaller = String(c.callerId) === String(me);
    if (!imCallee && !imCaller) return false;

    if (status === 'RINGING' && imCallee) {
      if (activeIncomingCallId === callId) return true;
      activeIncomingCallId = callId;
      isAccepted.value = false;
      const restoredType = String(c.type || 'VOICE').toUpperCase();
      session.value = {
        callId,
        type: restoredType,
        callerId: c.callerId,
        callerName: c.caller?.username || '',
        callerAvatar: c.caller?.profilePictureUrl || null,
        liveKitRoomId: c.liveKitRoomId,
        liveKitWsUrl: null,
        conversationId: c.conversationId,
        isIncoming: true,
        roomToken: null,
      };
      isVideoEnabled.value = restoredType === 'VIDEO';
      callState.value = CALL_STATES.RINGING;
      errorMessage.value = null;
      return true;
    }

    if (status === 'RINGING' && imCaller) {
      try {
        const tok = await getCallToken(callId);
        if (!tok.canJoin || !tok.token || !tok.liveKitWsUrl) return false;
        const ringingType = String(c.type || 'VOICE').toUpperCase();
        session.value = {
          callId,
          type: ringingType,
          isIncoming: false,
          receiverId: c.calleeId,
          calleeId: c.calleeId,
          calleeName: c.callee?.username || null,
          calleeAvatarUrl: c.callee?.profilePictureUrl || null,
          conversationId: c.conversationId,
          token: tok.token,
          liveKitWsUrl: tok.liveKitWsUrl,
          liveKitRoomId: tok.liveKitRoomId || c.liveKitRoomId,
          roomName: tok.liveKitRoomId || c.liveKitRoomId,
        };
        isVideoEnabled.value = ringingType === 'VIDEO';
        isAccepted.value = false;
        callState.value = CALL_STATES.RINGING;
        errorMessage.value = null;
        startOutgoingReconcilePolling();
        return true;
      } catch (e) {
        console.warn('[callStore] hydrate outgoing ringing failed', e);
        return false;
      }
    }

    if (status === 'ANSWERED') {
      try {
        const tok = await getCallToken(callId);
        if (!tok.canJoin || !tok.token || !tok.liveKitWsUrl) return false;
        const answeredType = String(c.type || 'VOICE').toUpperCase();
        isVideoEnabled.value = answeredType === 'VIDEO';
        if (imCallee) {
          session.value = {
            callId,
            type: answeredType,
            isIncoming: true,
            conversationId: c.conversationId,
            callerId: c.callerId,
            callerName: c.caller?.username || '',
            callerAvatar: c.caller?.profilePictureUrl || null,
            calleeId: c.calleeId,
            token: tok.token,
            liveKitWsUrl: tok.liveKitWsUrl,
            liveKitRoomId: tok.liveKitRoomId || c.liveKitRoomId,
            roomName: tok.liveKitRoomId || c.liveKitRoomId,
          };
        } else {
          session.value = {
            callId,
            type: answeredType,
            isIncoming: false,
            conversationId: c.conversationId,
            receiverId: c.calleeId,
            calleeId: c.calleeId,
            calleeName: c.callee?.username || null,
            calleeAvatarUrl: c.callee?.profilePictureUrl || null,
            callerId: c.callerId,
            token: tok.token,
            liveKitWsUrl: tok.liveKitWsUrl,
            liveKitRoomId: tok.liveKitRoomId || c.liveKitRoomId,
            roomName: tok.liveKitRoomId || c.liveKitRoomId,
          };
        }
        isAccepted.value = true;
        errorMessage.value = null;
        callState.value = CALL_STATES.ONGOING_ELSEWHERE;
        return true;
      } catch (e) {
        console.warn('[callStore] hydrate answered call failed', e);
        return false;
      }
    }

    return false;
  }

  /** After server says busy (e.g. CALLER_BUSY), load real call so user can rejoin instead of only a toast. */
  async function recoverActiveCallAfterServerBusy() {
    try {
      const res = await checkActiveCall();
      return await hydrateSessionFromActiveCheckResponse(res);
    } catch (e) {
      console.warn('[callStore] recoverActiveCallAfterServerBusy failed', e);
      return false;
    }
  }

  function isServerBusyAck(ack) {
    const r = String(ack.reason || ack.error || '').toUpperCase();
    return (
      ack.event === 'call:busy' ||
      r === 'CALLER_BUSY' ||
      r === 'CALLEE_BUSY' ||
      r === 'USER_BUSY' ||
      r === 'ACTIVE_RINGING_CALL'
    );
  }

  /**
   * Restore ringing / active call after tab focus or socket reconnect (item 5).
   */
  async function syncActiveCallFromServer(retryCount = 0) {
    let me = getCurrentUserId();
    if (!me) {
      if (retryCount < 5) {
        setTimeout(() => syncActiveCallFromServer(retryCount + 1), 1000);
      }
      return;
    }
    let res;
    try {
      res = await checkActiveCall();
    } catch (e) {
      console.warn('[callStore] checkActiveCall failed', e);
      return;
    }

    if (!res?.hasActiveCall || !res.call?.id) {
      if (hasActiveCall.value) {
        console.warn('[callStore] Local call active but server reports no active call. Ending ghost session.');
        errorMessage.value = 'Connection lost';
        setState(CALL_STATES.ENDED);
        cleanupRoom();
      }
      return;
    }

    if (hasActiveCall.value) {
      if (String(session.value?.callId) === String(res.call.id)) {
        return;
      }
      return;
    }

    await hydrateSessionFromActiveCheckResponse(res);
  }

  function scheduleActiveCallSync() {
    if (callLifecycleDebounceTimer) clearTimeout(callLifecycleDebounceTimer);
    callLifecycleDebounceTimer = setTimeout(() => {
      callLifecycleDebounceTimer = null;
      void syncActiveCallFromServer();
      void reconcileOutgoingCallWithServer();
    }, 900);
  }

  function installCallLifecycleSync() {
    stopCallLifecycleSync();
    callLifecycleInstalled = true;
    const socket = getSocketServiceInstance();
    callLifecycleConnSuccess = () => scheduleActiveCallSync();
    socket.on('connectionSuccess', callLifecycleConnSuccess);
    callLifecycleVisibility = () => {
      if (document.visibilityState === 'visible') scheduleActiveCallSync();
    };
    document.addEventListener('visibilitychange', callLifecycleVisibility);
    if (callLifecycleInitialTimer) clearTimeout(callLifecycleInitialTimer);
    callLifecycleInitialTimer = setTimeout(() => {
      callLifecycleInitialTimer = null;
      scheduleActiveCallSync();
    }, 1200);
  }

  function stopCallLifecycleSync() {
    clearOutgoingReconcileTimer();
    removeCallSwMessageBridge();
    if (callLifecycleInitialTimer) {
      clearTimeout(callLifecycleInitialTimer);
      callLifecycleInitialTimer = null;
    }
    const socket = getSocketServiceInstance();
    if (callLifecycleConnSuccess) {
      socket.off('connectionSuccess', callLifecycleConnSuccess);
      callLifecycleConnSuccess = null;
    }
    if (callLifecycleVisibility) {
      document.removeEventListener('visibilitychange', callLifecycleVisibility);
      callLifecycleVisibility = null;
    }
    if (callLifecycleDebounceTimer) {
      clearTimeout(callLifecycleDebounceTimer);
      callLifecycleDebounceTimer = null;
    }
    callLifecycleInstalled = false;
  }

  function initiateCall(conversationId, calleeId, isVideo, calleeName, calleeAvatarUrl) {
    if (hasActiveCall.value) {
      errorMessage.value = 'Already in a call';
      return Promise.resolve(false);
    }
    const liveStore = useLiveStore();
    if (liveStore.isInSession) {
      if (liveStore.isHost) void liveStore.endLive();
      else if (liveStore.isViewer) void liveStore.leaveLive();
    }
    const socket = getSocketServiceInstance();
    if (!socket.isConnected()) {
      errorMessage.value = 'Not connected';
      return Promise.resolve(false);
    }
    const type = isVideo ? 'VIDEO' : 'VOICE';
    const clientRequestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    session.value = {
      conversationId,
      calleeId,
      type,
      isIncoming: false,
      receiverId: calleeId,
      calleeName: calleeName || null,
      calleeAvatarUrl: calleeAvatarUrl || null,
    };
    isVideoEnabled.value = isVideo;
    isAccepted.value = false;
    callState.value = CALL_STATES.DIALING;
    errorMessage.value = null;
    return new Promise((resolve) => {
      socket.emit('call:initiate', {
        calleeId,
        type,
        conversationId: conversationId || undefined,
        clientRequestId,
      }, (data) => {
        const ack = normalizeAck(data);
        if (!ack.success) {
          if (isServerBusyAck(ack)) {
            void recoverActiveCallAfterServerBusy().then((ok) => {
              if (ok) {
                errorMessage.value = null;
              } else {
                errorMessage.value = ack.error || ack.reason || 'Call failed';
                setState(CALL_STATES.ENDED);
              }
            });
            resolve(false);
            return;
          }
          errorMessage.value = ack.error || ack.reason || 'Call failed';
          setState(CALL_STATES.ENDED);
          resolve(false);
          return;
        }
        const token = ack.token;
        const liveKitRoomId = ack.liveKitRoomId ?? ack.call?.liveKitRoomId;
        const liveKitWsUrl = ack.liveKitWsUrl ?? ack.call?.liveKitWsUrl;
        const callId = ack.callId ?? ack.call?.id ?? liveKitRoomId;
        if (!token || !liveKitRoomId || !liveKitWsUrl) {
          errorMessage.value = 'No token or room info';
          setState(CALL_STATES.ENDED);
          resolve(false);
          return;
        }
        session.value = {
          ...session.value,
          callId,
          token,
          liveKitRoomId,
          liveKitWsUrl,
          roomName: liveKitRoomId,
        };
        callState.value = CALL_STATES.RINGING;
        startOutgoingReconcilePolling();
        resolve(true);
      });
    });
  }

  function isSameIncomingSession(sess) {
    return (
      session.value &&
      sess?.callId &&
      String(session.value.callId) === String(sess.callId) &&
      session.value.isIncoming === true
    );
  }

  async function offerRejoinOrCloseAfterIncomingAcceptFailure(sess) {
    if (!sess?.callId) {
      setState(CALL_STATES.ENDED);
      return;
    }
    // Reject / end while accept was in flight — do not resurrect UI from stale closure.
    if (!isSameIncomingSession(sess)) {
      return;
    }
    try {
      const res = await checkActiveCall();
      if (!isSameIncomingSession(sess)) return;
      if (res?.hasActiveCall && String(res.call.id) === String(sess.callId)) {
        const st = String(res.call.status || '').toUpperCase();
        if (st === 'ANSWERED' || st === 'RINGING') {
          const tok = await getCallToken(sess.callId);
          if (!isSameIncomingSession(sess)) return;
          if (tok.canJoin && tok.token && tok.liveKitWsUrl) {
            const c = res.call;
            const cur = session.value;
            session.value = {
              ...cur,
              token: tok.token,
              liveKitWsUrl: tok.liveKitWsUrl,
              liveKitRoomId: tok.liveKitRoomId || c.liveKitRoomId || cur.liveKitRoomId,
              roomName: tok.liveKitRoomId || c.liveKitRoomId || cur.roomName || cur.liveKitRoomId,
            };
            isAccepted.value = true;
            errorMessage.value = null;
            callState.value = CALL_STATES.CONNECTING;
            connectToLiveKit(session.value);
            return;
          }
        }
        if (st === 'RINGING') {
          if (!isSameIncomingSession(sess)) return;
          errorMessage.value = null;
          callState.value = CALL_STATES.RINGING;
          return;
        }
      }
    } catch (e) {
      console.warn('[callStore] offerRejoinOrCloseAfterIncomingAcceptFailure', e);
    }
    if (!isSameIncomingSession(sess)) return;
    errorMessage.value = null;
    callState.value = CALL_STATES.INCOMING_UNRESOLVED;
  }

  async function rejoinPendingIncomingCall() {
    const sess = session.value;
    if (!sess?.callId || callState.value !== CALL_STATES.INCOMING_UNRESOLVED) return;
    try {
      const res = await checkActiveCall();
      if (!res?.hasActiveCall || String(res.call.id) !== String(sess.callId)) {
        errorMessage.value = 'Call no longer available';
        setState(CALL_STATES.ENDED);
        return;
      }
      const st = String(res.call.status || '').toUpperCase();
      if (st !== 'RINGING' && st !== 'ANSWERED') {
        errorMessage.value = 'Call no longer available';
        setState(CALL_STATES.ENDED);
        return;
      }
      const tok = await getCallToken(sess.callId);
      if (!tok.canJoin || !tok.token || !tok.liveKitWsUrl) {
        errorMessage.value =
          st === 'ANSWERED'
            ? 'Call was answered on another device or has ended'
            : 'Call no longer available';
        return;
      }
      const c = res.call;
      session.value = {
        ...sess,
        token: tok.token,
        liveKitWsUrl: tok.liveKitWsUrl,
        liveKitRoomId: tok.liveKitRoomId || c.liveKitRoomId || sess.liveKitRoomId,
        roomName: tok.liveKitRoomId || c.liveKitRoomId || sess.liveKitRoomId,
      };
      isAccepted.value = true;
      errorMessage.value = null;
      callState.value = CALL_STATES.CONNECTING;
      connectToLiveKit(session.value);
    } catch (e) {
      errorMessage.value = e?.message || 'Failed to get token';
    }
  }

  function dismissPendingIncomingCall() {
    if (session.value?.callId) {
      blockedCallIds.add(session.value.callId);
    }
    setState(CALL_STATES.ENDED);
  }

  function resetForSessionLogout() {
    stopCallLifecycleSync();
    if (typeof socketOffFns === 'function') {
      socketOffFns();
      socketOffFns = [];
    }
    clearOutgoingReconcileTimer();
    activeIncomingCallId = null;
    if (session.value?.callId) {
      blockedCallIds.add(session.value.callId);
    }
    cleanupRoom();
    session.value = null;
    callState.value = CALL_STATES.IDLE;
    errorMessage.value = null;
    isAccepted.value = false;
    isMuted.value = false;
    isVideoEnabled.value = true;
  }

  async function acceptCall() {
    const sess = session.value;
    if (!sess || !sess.isIncoming) return false;
    const socket = getSocketServiceInstance();
    if (socket.isConnected()) {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        void offerRejoinOrCloseAfterIncomingAcceptFailure(sess);
      }, ACCEPT_ACK_TIMEOUT_MS);

      socket.emit('call:accept', { callId: sess.callId }, (data) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);

        const ack = normalizeAck(data);
        if (!ack.success) {
          void offerRejoinOrCloseAfterIncomingAcceptFailure(sess);
          return;
        }
        const callObj = ack.call && typeof ack.call === 'object' ? ack.call : null;
        const hasCallRecord = callObj != null && Object.keys(callObj).length > 0;
        const tokenFromAck = ack.token != null && String(ack.token).trim() !== '' ? ack.token : null;
        const roomTok =
          sess.roomToken && String(sess.roomToken).trim() !== '' ? sess.roomToken : null;
        let token =
          tokenFromAck ??
          (sess.token && String(sess.token).trim() !== '' ? sess.token : null) ??
          roomTok;
        const liveKitWsUrl = ack.liveKitWsUrl ?? callObj?.liveKitWsUrl ?? sess.liveKitWsUrl;
        let roomName = ack.liveKitRoomId ?? callObj?.liveKitRoomId ?? sess.liveKitRoomId ?? sess.callId;
        // Multi-device race: ack success but empty token (and no ack token) — fetch join token or show friendly error.
        const emptyAckTokenRace = !tokenFromAck && !hasCallRecord;
        const hasWs = Boolean(liveKitWsUrl);
        const mustResolveViaApi =
          !token ||
          ack.alreadyHandled === true ||
          emptyAckTokenRace ||
          (Boolean(token) && !hasWs);
        const canUseAckOnly =
          Boolean(tokenFromAck) && hasWs && !ack.alreadyHandled && !emptyAckTokenRace;
        if (mustResolveViaApi && !canUseAckOnly) {
          getCallToken(sess.callId).then((res) => {
            if (callState.value === CALL_STATES.ENDED || session.value?.callId !== sess.callId) return;
            if (!res.canJoin || !res.token) {
              void offerRejoinOrCloseAfterIncomingAcceptFailure(sess);
              return;
            }
            session.value = {
              ...sess,
              token: res.token,
              liveKitWsUrl: res.liveKitWsUrl ?? liveKitWsUrl,
              roomName: res.liveKitRoomId || sess.callId,
              liveKitRoomId: res.liveKitRoomId || sess.callId,
            };
            callState.value = CALL_STATES.CONNECTING;
            isAccepted.value = true;
            connectToLiveKit(session.value);
          }).catch(() => {
            void offerRejoinOrCloseAfterIncomingAcceptFailure(sess);
          });
          return;
        }
        if (callState.value === CALL_STATES.ENDED || session.value?.callId !== sess.callId) return;
        session.value = { ...sess, token, liveKitWsUrl, roomName, liveKitRoomId: roomName };
        callState.value = CALL_STATES.CONNECTING;
        isAccepted.value = true;
        connectToLiveKit(session.value);
      });
    } else {
      try {
        const res = await getCallToken(sess.callId);
        if (callState.value === CALL_STATES.ENDED || session.value?.callId !== sess.callId) return false;
        if (!res.canJoin || !res.token) {
          await offerRejoinOrCloseAfterIncomingAcceptFailure(sess);
          return false;
        }
        session.value = { ...sess, token: res.token, liveKitWsUrl: res.liveKitWsUrl, roomName: res.liveKitRoomId || sess.callId, liveKitRoomId: res.liveKitRoomId || sess.callId };
        callState.value = CALL_STATES.CONNECTING;
        isAccepted.value = true;
        connectToLiveKit(session.value);
      } catch (e) {
        await offerRejoinOrCloseAfterIncomingAcceptFailure(sess);
        return false;
      }
    }
    return true;
  }

  async function rejectCall() {
    const sess = session.value;
    if (!sess?.callId) return;
    const socket = getSocketServiceInstance();
    if (socket.isConnected()) {
      socket.emit('call:reject', { callId: sess.callId });
    } else {
      try {
        await rejectCallFromNotificationRest(sess.callId);
      } catch (e) {
        console.warn('[callStore] rejectCallFromNotificationRest failed', e);
      }
    }
    setState(CALL_STATES.ENDED);
  }

  async function endCall() {
    const sess = session.value;
    if (sess?.callId) {
      const socket = getSocketServiceInstance();
      if (socket.isConnected()) {
        socket.emit('call:end', { callId: sess.callId, reason: 'COMPLETED' });
      } else {
        try {
          await endCallRest(sess.callId, 'COMPLETED');
        } catch (e) {
          console.warn('[callStore] endCallRest failed', e);
        }
      }
    }
    setState(CALL_STATES.ENDED);
    cleanupRoom();
  }

  async function cancelCall() {
    const sess = session.value;
    if (sess?.callId) {
      const socket = getSocketServiceInstance();
      if (socket.isConnected()) {
        socket.emit('call:cancel', { callId: sess.callId });
      } else {
        try {
          await cancelCallRest(sess.callId);
        } catch (e) {
          console.warn('[callStore] cancelCallRest failed', e);
        }
      }
    }
    setState(CALL_STATES.ENDED);
    cleanupRoom();
  }

  function toggleMute() {
    if (!room.value) return;
    isMuted.value = !isMuted.value;
    room.value.localParticipant.setMicrophoneEnabled(!isMuted.value);
  }

  async function toggleVideo() {
    if (!room.value?.localParticipant || !session.value) return;
    const lp = room.value.localParticipant;
    const next = !isVideoEnabled.value;
    try {
      await lp.setCameraEnabled(next);
      isVideoEnabled.value = next;
      if (!next) {
        localVideoTrack.value = null;
      } else {
        syncLocalVideoFromParticipant(lp);
      }
    } catch (e) {
      console.warn('[callStore] toggleVideo failed', e);
      const msg = e && e.message ? String(e.message) : '';
      if (/NotAllowed|Permission|denied/i.test(msg)) {
        errorMessage.value = 'Camera permission denied';
      } else if (msg) {
        errorMessage.value = 'Could not toggle camera';
      }
    }
  }

  async function joinOngoingCall() {
    if (callState.value !== CALL_STATES.ONGOING_ELSEWHERE) return;
    if (!session.value || !session.value.token || !session.value.liveKitWsUrl) return;
    callState.value = CALL_STATES.CONNECTING;
    await connectToLiveKit(session.value);
  }

  function init() {
    attachSocketListeners();
    installCallSwMessageBridge();
    void registerCallInviteWorker();
    installCallLifecycleSync();
  }

  return {
    session,
    callState,
    errorMessage,
    room,
    localVideoTrack,
    remoteVideoTrack,
    remoteAudioTrack,
    isMuted,
    isVideoEnabled,
    hasActiveCall,
    hasIncomingCall,
    isVideoCall,
    showCallScreen,
    remoteDisplayName,
    remoteAvatarUrl,
    isAccepted,
    CALL_STATES,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    cancelCall,
    joinOngoingCall,
    rejoinPendingIncomingCall,
    dismissPendingIncomingCall,
    resetForSessionLogout,
    toggleMute,
    toggleVideo,
    setState,
    cleanupRoom,
    init,
    stopCallLifecycleSync,
    syncActiveCallFromServer,
  };
});
